import { Button, Modal } from "antd";
import { DeleteOutlined } from "@ant-design/icons";

const BRAND_BUTTON = { backgroundColor: "#b4c540", borderColor: "#b4c540" };

/**
 * The one confirmation for destructive actions.
 *
 * Replaces the browser `confirm()`, antd `Popconfirm` and the per-page
 * "Are you sure?" modals that used to coexist. The title must name the record
 * ("Delete James Wood Horn Co?") so the admin can tell *what* a misclick would
 * take with it; the description says what happens and that it is permanent.
 *
 * `danger` (default) styles the confirm red for deletes; a non-destructive
 * action such as "Reactivate" passes `danger={false}` and gets the brand
 * colour and its own `icon` instead of the trash can.
 *
 * Purely presentational — `useConfirmDialog` owns the open/loading state and
 * the pages own the mutation and its toasts.
 */
const ConfirmDialog = ({
  open,
  title,
  description,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  loading = false,
  danger = true,
  icon,
  onConfirm,
  onCancel,
}) => (
  <Modal
    open={open}
    onCancel={onCancel}
    footer={null}
    closable={false}
    // While the mutation is in flight the dialog must not be dismissable:
    // closing it would hide the spinner but not stop the delete.
    maskClosable={!loading}
    keyboard={!loading}
    centered
    width={400}
    destroyOnClose
  >
    <div className="flex flex-col items-center pt-2 text-center">
      <span
        className={`flex h-12 w-12 items-center justify-center rounded-full ${
          danger ? "bg-red/10 text-red" : "bg-primary/20 text-primary_dark"
        }`}
        style={{ fontSize: 22 }}
      >
        {icon ?? <DeleteOutlined />}
      </span>

      <h3 className="mt-4 text-lg font-semibold text-black">{title}</h3>

      {description && (
        <p className="mt-2 text-sm text-secondary">{description}</p>
      )}

      <div className="mt-6 flex w-full justify-center gap-3">
        <Button onClick={onCancel} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button
          type="primary"
          danger={danger}
          loading={loading}
          onClick={onConfirm}
          // antd's primary is blue; inline wins over its generated class.
          style={danger ? undefined : BRAND_BUTTON}
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  </Modal>
);

export default ConfirmDialog;
